import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, MessageCircleQuestion } from "lucide-react";
import { api } from "@/lib/api";

export default function LandingCurriculum() {
  const navigate = useNavigate();
  const [standard, setStandard] = useState(6);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    api.get("/curriculum/tree", { params: { board: "CBSE", standard } })
      .then(({ data }) => setTree(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [standard]);

  return (
    <section id="curriculum" className="py-10 sm:py-16 bg-slate-50/70 border-b border-slate-100" data-testid="landing-curriculum-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-primary">Real CBSE Syllabus</p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Browse the actual curriculum inside SmartGuru</h2>
        </div>
        <div className="flex justify-center flex-wrap gap-2 mb-8">
          {[5, 6, 7, 8, 9].map((s) => (
            <button
              key={s}
              onClick={() => setStandard(s)}
              data-testid={`curriculum-tab-class-${s}`}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${standard === s ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground"}`}
            >
              Class {s}
            </button>
          ))}
        </div>
        {loading ? (
          <p className="text-center text-sm text-muted-foreground" data-testid="curriculum-loading">Loading real syllabus...</p>
        ) : error ? (
          <p className="text-center text-sm text-destructive" data-testid="curriculum-error">Couldn't load the syllabus right now — please try again shortly.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {tree.map((subject) => (
              <div key={subject.name} className="bg-card border border-border rounded-2xl p-5" data-testid={`curriculum-subject-${subject.name.toLowerCase()}`}>
                <h3 className="font-heading font-bold flex items-center gap-2 mb-3"><BookOpen size={16} className="text-primary" />{subject.name}</h3>
                <ul className="space-y-1.5 max-h-52 overflow-y-auto scrollbar-thin">
                  {subject.chapters.map((ch) => (
                    <li key={ch.name} className="text-sm text-muted-foreground flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0" /> {ch.name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <div className="text-center mt-10">
          <button
            onClick={() => navigate("/contact?topic_request=true")}
            data-testid="request-topic-button"
            className="inline-flex items-center gap-2 bg-card border border-border rounded-full px-5 py-3 text-sm font-semibold text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
          >
            <MessageCircleQuestion size={16} className="text-primary" />
            Finding a topic? Want us to add a specific chapter? Click here.
          </button>
        </div>
      </div>
    </section>
  );
}
